<template>
  <div class="cust">
    <div class="cust__wrap">
      <app-icon name="search" :size="16" class="cust__search" />
      <input
        :id="inputId"
        ref="input"
        v-model="query"
        type="text"
        class="cust__input"
        :class="{ 'cust__input--clear': query && !disabled }"
        role="combobox"
        aria-autocomplete="list"
        :aria-expanded="open ? 'true' : 'false'"
        :aria-controls="listId"
        :aria-activedescendant="active >= 0 && open ? optionId(active) : null"
        :aria-describedby="hintId"
        autocomplete="off"
        autocapitalize="none"
        spellcheck="false"
        placeholder="Kundennummer oder Name"
        :disabled="disabled"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
        @keydown="onKeydown"
      />
      <button
        v-if="query && !disabled"
        type="button"
        class="cust__clear"
        tabindex="-1"
        aria-label="Kunde ändern"
        @mousedown.prevent
        @click="clear"
      >
        <app-icon name="x" :size="14" />
      </button>
    </div>
    <ul v-show="open" :id="listId" class="cust__list" role="listbox" aria-label="Kunden">
      <li
        v-for="(customer, index) in matches"
        :id="optionId(index)"
        :key="customer.customer_id"
        class="cust__option"
        :class="{ 'cust__option--active': index === active }"
        role="option"
        :aria-selected="index === active ? 'true' : 'false'"
        @mousedown.prevent="choose(customer)"
        @mousemove="active = index"
      >
        <span class="cust__name">{{ customer.name }}</span>
        <span class="cust__nr">{{ customer.customer_id }}</span>
      </li>
      <li v-if="!matches.length" class="cust__empty" role="presentation">{{ emptyText }}</li>
    </ul>
    <span :id="hintId" class="app-sr-only">Mindestens zwei Zeichen oder die Kundennummer eingeben, dann aus der Liste wählen.</span>
    <span class="app-sr-only" role="status" aria-live="polite">{{ statusText }}</span>
  </div>
</template>

<script>
// Kundenauswahl der Anmeldung als Suchfeld (Kundenwunsch 13.10.2026: „die kundenauswahl vielleicht noch irgendwie
// schlauer machen, dass man das nicht sofort alles sieht und nicht dieses ewig lange dropdown hat“): statt der langen
// Auswahlliste ein Feld „Kundennummer oder Name“; Vorschläge erst ab zwei Zeichen oder einer Ziffer, höchstens acht
// (Suche und Reihenfolge in src/utils/customerSearch.js, wie beim Mandantenwechsel). Tastatur nach dem
// ARIA-Combobox-Muster (Pfeile, Enter wählt, Esc schließt bzw. leert; das x zum Leeren nur per Maus/Touch, Tab führt
// direkt zum Benutzernamen). Die Wahl zeigt „60 Edzards Reisen“ wie bisher
// die Liste; jede Änderung am Text hebt sie auf, bis neu gewählt wird; beim Verlassen wird ein genauer Treffer (Nummer
// oder ganzer Name) übernommen. v-model = customer_id (null ohne Wahl); Ereignis chosen nach einer Wahl (die Seite
// springt dann zum Benutzernamen).
import { customerLabel as label, customerQueryReady, searchCustomers, exactCustomer } from "@/utils/customerSearch";

export default {
  name: "LoginCustomerField",
  props: {
    value: { type: Number, default: null },
    customers: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    inputId: { type: String, default: "login-customer" }
  },
  data() {
    return {
      query: "",
      open: false,
      active: -1
    };
  },
  computed: {
    listId() {
      return this.inputId + "-list";
    },
    hintId() {
      return this.inputId + "-hint";
    },
    selected() {
      return this.customers.find((c) => c.customer_id === this.value) || null;
    },
    // erst ab zwei Zeichen oder einer Ziffer suchen (src/utils/customerSearch.js)
    canSearch() {
      return customerQueryReady(this.query);
    },
    matches() {
      return searchCustomers(this.customers, this.query);
    },
    emptyText() {
      return this.canSearch ? "Kein Kunde gefunden" : "Mindestens 2 Zeichen eingeben";
    },
    statusText() {
      if (!this.open) {
        return "";
      }
      if (!this.matches.length) {
        return this.emptyText;
      }
      return this.matches.length === 1 ? "1 Kunde gefunden" : this.matches.length + " Kunden gefunden";
    }
  },
  watch: {
    // Vorbelegung (zuletzt gewählter Kunde) und Wahl von außen im Feld zeigen
    selected: {
      immediate: true,
      handler(customer) {
        if (customer) {
          this.query = label(customer);
        }
      }
    }
  },
  methods: {
    optionId(index) {
      return this.inputId + "-opt-" + index;
    },
    exactMatch() {
      return exactCustomer(this.customers, this.query);
    },
    onInput() {
      if (this.value !== null) {
        this.$emit("input", null);
      }
      this.open = this.query.trim().length > 0;
      this.active = this.matches.length ? 0 : -1;
    },
    // gewählter Kunde: Text markieren, damit Tippen ihn ersetzt
    onFocus() {
      if (this.selected) {
        this.$nextTick(() => this.$refs.input && this.$refs.input.select());
      } else if (this.query.trim()) {
        this.open = true;
      }
    },
    onBlur() {
      this.open = false;
      if (this.value === null) {
        const exact = this.exactMatch();
        if (exact) {
          this.$emit("input", exact.customer_id);
          this.query = label(exact);
        }
      }
    },
    onKeydown(e) {
      const n = this.matches.length;
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        if (!this.open) {
          this.open = this.query.trim().length > 0;
          return;
        }
        if (n) {
          const step = e.key === "ArrowDown" ? 1 : -1;
          this.active = (this.active + step + n) % n;
        }
      } else if (e.key === "Enter") {
        // offene Liste: Enter wählt statt abzuschicken
        if (this.open && n) {
          e.preventDefault();
          this.choose(this.matches[this.active >= 0 ? this.active : 0]);
        } else if (this.value === null) {
          const exact = this.exactMatch();
          if (exact) {
            e.preventDefault();
            this.choose(exact);
          }
        }
      } else if (e.key === "Escape") {
        if (this.open) {
          e.preventDefault();
          this.open = false;
        } else if (this.query && this.value === null) {
          e.preventDefault();
          this.query = "";
        }
      }
    },
    choose(customer) {
      this.query = label(customer);
      this.open = false;
      this.active = -1;
      this.$emit("input", customer.customer_id);
      this.$emit("chosen", customer.customer_id);
    },
    clear() {
      this.query = "";
      this.open = false;
      this.active = -1;
      if (this.value !== null) {
        this.$emit("input", null);
      }
      this.focus();
    },
    focus() {
      if (this.$refs.input) {
        this.$refs.input.focus();
      }
    }
  }
};
</script>

<style scoped>
.cust {
  position: relative;
}
.cust__wrap {
  position: relative;
}
.cust__search {
  position: absolute;
  top: 50%;
  left: 12px;
  transform: translateY(-50%);
  color: var(--ink-3);
  pointer-events: none;
}
/* Feld wie die übrigen Felder der Anmeldung, links Platz für die Lupe */
.cust__input {
  width: 100%;
  height: 36px;
  box-sizing: border-box;
  margin: 0;
  padding: 0 12px 0 36px;
  border: 1px solid var(--field-edge);
  border-radius: 6px;
  background: #fff;
  color: var(--ink);
  font-family: inherit;
  font-size: 14px;
  line-height: 20px;
  text-overflow: ellipsis;
}
.cust__input--clear {
  padding-right: 36px;
}
.cust__input::placeholder {
  color: var(--ink-3);
}
.cust__input:hover:not(:disabled) {
  border-color: var(--field-edge-hover);
}
.cust__input:focus-visible {
  outline: 2px solid var(--link);
  outline-offset: 2px;
}
.cust__input:disabled {
  background: var(--dis-bg);
  color: var(--ink-3);
}
.cust__clear {
  position: absolute;
  top: 50%;
  right: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin: 0;
  padding: 0;
  transform: translateY(-50%);
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: var(--ink-3);
  cursor: pointer;
}
.cust__clear:hover {
  background: var(--hov);
  color: var(--ink);
}
.cust__clear:focus-visible {
  outline: 2px solid var(--link);
  outline-offset: 1px;
}
/* Vorschläge unter dem Feld wie ein Menü */
.cust__list {
  position: absolute;
  z-index: 5;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-height: 296px;
  overflow-y: auto;
  margin: 0;
  padding: 4px;
  list-style: none;
  border: 1px solid var(--line);
  border-radius: 6px;
  background: #fff;
  box-shadow: var(--sh-menu);
}
.cust__option {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 36px;
  padding: 0 8px;
  border-radius: 4px;
  font-size: 14px;
  line-height: 20px;
  color: var(--ink);
  cursor: pointer;
}
.cust__option--active {
  background: var(--hov);
}
.cust__name {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cust__nr {
  flex: none;
  font-size: 12px;
  color: var(--ink-3);
  font-variant-numeric: tabular-nums;
}
.cust__empty {
  padding: 8px;
  font-size: 13px;
  line-height: 20px;
  color: var(--ink-3);
}
</style>
